import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Apple, CloudOff, Minus, Plus, RefreshCw, ScanLine, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import harvestService from '../../services/harvest';
import jobService from '../../services/jobs';
import fieldWorkService from '../../services/fieldWork';
import {
  finishBinQueued,
  flushHarvestQueue,
  pendingHarvestCount,
  startBinsQueued,
  subscribeHarvestQueue,
} from '../../services/harvestQueue';
import { requestLocationWithDisclosure } from '../../services/locationGate';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { workerDisplayName, type Worker } from '../../types/fieldWork';
import { binPlace, type Bin, type PickingJob } from '../../types/harvest';

type Result =
  | { kind: 'started'; bins: Bin[] }
  | { kind: 'queued'; codes: string[] };

/**
 * /harvest/pick — start bins where you stand. For pickers (no seat: they only
 * need to be on the job) and for supervisors starting bins for their crew.
 * One tap takes a GPS fix, the server places the bin and proposes the code to
 * chalk on it. No signal: the start is queued with its time and fix, and the
 * phone gives its own code to chalk.
 */
export default function PickBins() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const [params] = useSearchParams();
  const { hasAccess: supervising } = useModuleLicense('farm_jobs', ['supervisor', 'creator', 'admin']);

  const [jobs, setJobs] = useState<PickingJob[]>([]);
  const [jobId, setJobId] = useState<number | null>(params.get('job') ? Number(params.get('job')) : null);
  const [roster, setRoster] = useState<Worker[]>([]);
  const [pickers, setPickers] = useState<number[]>([]);
  const [count, setCount] = useState(1);
  const [chalk, setChalk] = useState('');
  const [row, setRow] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [todayBins, setTodayBins] = useState<Bin[]>([]);
  const [pending, setPending] = useState(pendingHarvestCount());
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => subscribeHarvestQueue(() => setPending(pendingHarvestCount())), []);

  const loadBins = useCallback(async () => {
    try {
      setTodayBins(supervising && jobId
        ? await harvestService.listBins({ job_id: jobId, from: today, to: today })
        : await harvestService.myBins(today));
    } catch { /* offline: keep what is shown */ }
  }, [supervising, jobId, today]);

  const sync = useCallback(async () => {
    const r = await flushHarvestQueue().catch(() => null);
    if (r?.refused.length) setError(r.refused.join(' · '));
    await loadBins();
  }, [loadBins]);

  useEffect(() => {
    let live = true;
    harvestService.myJobs()
      .then(async j => {
        // A supervisor opening a job they are not assigned to (from its page) still gets it.
        if (jobId && !j.some(x => x.id === jobId)) {
          const r = await jobService.getJob(jobId).catch(() => null);
          if (r?.success) {
            j = [{ id: r.data.id, name: r.data.name, status: r.data.status, target_name: r.data.target_name,
              farm_id: r.data.farm_id, farm_name: null, activity_type_name: r.data.activity_type_name ?? null }, ...j];
          }
        }
        if (!live) return;
        setJobs(j);
        if (!jobId && j.length === 1) setJobId(j[0].id);
      })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load your jobs')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (supervising) fieldWorkService.getWorkers().then(setRoster).catch(() => setRoster([]));
  }, [supervising]);

  useEffect(() => { sync(); }, [sync]);
  useEffect(() => {
    const on = () => { sync(); };
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, [sync]);

  const job = useMemo(() => jobs.find(j => j.id === jobId) ?? null, [jobs, jobId]);
  const chalkCodes = chalk.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);

  const start = async () => {
    if (!jobId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fix = await requestLocationWithDisclosure({ enableHighAccuracy: true, timeout: 10_000, maximumAge: 15_000 });
      const r = await startBinsQueued({
        job_id: jobId,
        count: chalkCodes.length || count,
        field_codes: chalkCodes.length ? chalkCodes : undefined,
        worker_ids: pickers,
        lat: fix?.coords.latitude ?? null,
        lng: fix?.coords.longitude ?? null,
        accuracy: fix?.coords.accuracy ?? null,
        row_number: row ? Number(row) : null,
      }, today);
      setResult(r.queued ? { kind: 'queued', codes: r.field_codes } : { kind: 'started', bins: r.bins });
      setChalk('');
      await loadBins();
    } catch (e) {
      setError(errorMessage(e, 'Could not start the bins'));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (b: Bin, fill: number) => {
    try {
      await finishBinQueued(b.id, fill);
      await loadBins();
    } catch (e) {
      setError(errorMessage(e, 'Could not update the bin'));
    }
  };

  const onScan = (code: string) => {
    const w = roster.find(x => x.picker_code.toUpperCase() === code.trim().toUpperCase());
    if (w) setPickers(p => (p.includes(w.id) ? p : [...p, w.id]));
    setScanning(false);
  };

  if (loading) return <div className="max-w-xl mx-auto px-4 py-12 text-center text-sm text-neutral-500">Loading…</div>;

  return (
    <>
      <PageHeader title="Start bins" subtitle={job?.name ?? 'Harvest'} icon={<Apple className="h-6 w-6 text-white" />} />
      <div className="max-w-xl mx-auto px-4 py-4 space-y-4">
        {pending > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <CloudOff className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 min-w-0">{pending} waiting to send — times and positions are kept.</span>
            <Button size="sm" variant="outline" onClick={sync}><RefreshCw className="h-4 w-4" /> Send now</Button>
          </div>
        )}
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {jobs.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-500">
            You are not on a picking job yet. Your supervisor adds you to one.
            {supervising && (
              <div className="mt-3"><Button size="sm" variant="outline" onClick={() => navigate('/jobs')}>Go to Farm Jobs</Button></div>
            )}
          </div>
        ) : (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4">
            {jobs.length > 1 && (
              <label className="block text-sm">
                <span className="block font-medium text-neutral-700 mb-1">Job</span>
                <select value={jobId ?? ''} onChange={e => setJobId(Number(e.target.value) || null)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Choose…</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.name}{j.target_name ? ` — ${j.target_name}` : ''}</option>)}
                </select>
              </label>
            )}

            {supervising && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-700">Pickers</p>
                <div className="flex flex-wrap gap-2">
                  {pickers.map(id => {
                    const w = roster.find(x => x.id === id);
                    return (
                      <span key={id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-800">
                        {w ? workerDisplayName(w) : `#${id}`}
                        <button type="button" aria-label="Remove" onClick={() => setPickers(p => p.filter(x => x !== id))}><X className="h-3.5 w-3.5" /></button>
                      </span>
                    );
                  })}
                  {pickers.length === 0 && <span className="text-sm text-neutral-500">You, unless you add someone.</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setScanning(true)}><ScanLine className="h-4 w-4" /> Scan card</Button>
                  <select aria-label="Add a picker" value="" onChange={e => e.target.value && setPickers(p => [...new Set([...p, Number(e.target.value)])])}
                    className="min-w-0 max-w-full flex-1 border border-neutral-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                    <option value="">Add a picker…</option>
                    {roster.filter(w => !pickers.includes(w.id)).map(w => <option key={w.id} value={w.id}>{workerDisplayName(w)} ({w.picker_code})</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-neutral-700">How many bins</span>
              <div className="flex items-center gap-2">
                <button type="button" aria-label="Fewer" disabled={count <= 1 || chalkCodes.length > 0} onClick={() => setCount(c => Math.max(1, c - 1))}
                  className="h-10 w-10 rounded-lg border border-neutral-300 flex items-center justify-center disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                <span className="w-8 text-center text-lg font-semibold" aria-live="polite">{chalkCodes.length || count}</span>
                <button type="button" aria-label="More" disabled={count >= 10 || chalkCodes.length > 0} onClick={() => setCount(c => Math.min(10, c + 1))}
                  className="h-10 w-10 rounded-lg border border-neutral-300 flex items-center justify-center disabled:opacity-40"><Plus className="h-4 w-4" /></button>
              </div>
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-neutral-600">Already chalked a code, or know the row?</summary>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <label className="block">
                  <span className="block text-xs text-neutral-600 mb-1">Chalk code(s)</span>
                  <input value={chalk} onChange={e => setChalk(e.target.value.toUpperCase())} placeholder="e.g. 14-07"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm uppercase" />
                </label>
                <label className="block">
                  <span className="block text-xs text-neutral-600 mb-1">Row</span>
                  <input value={row} onChange={e => setRow(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="From GPS"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </label>
              </div>
            </details>

            <Button variant="primary" size="lg" className="w-full" disabled={!jobId || busy} onClick={start}>
              {busy ? 'Getting your position…' : `Start ${chalkCodes.length || count} ${(chalkCodes.length || count) === 1 ? 'bin' : 'bins'} here`}
            </Button>

            {result?.kind === 'started' && (
              <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4 text-center" role="status">
                <p className="text-sm text-green-800">Chalk on the bin{result.bins.length > 1 ? 's' : ''}:</p>
                <p className="text-3xl font-bold tracking-wide text-green-900 break-words">{result.bins.map(b => b.field_code).join('  ')}</p>
                <p className="text-sm text-green-800 mt-1">
                  {binPlace(result.bins[0])}{result.bins[0].row_number ? ` · row ${result.bins[0].row_number}` : ''}
                </p>
                {result.bins[0].needs_review && (
                  <p className="text-xs text-amber-800 mt-2">No good GPS fix — the shed will confirm the block.</p>
                )}
              </div>
            )}
            {result?.kind === 'queued' && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-center" role="status">
                <p className="text-sm text-amber-800">No signal. Chalk this code — it will send when you are back in range:</p>
                <p className="text-3xl font-bold tracking-wide text-amber-900 break-words">{result.codes.join('  ')}</p>
              </div>
            )}
          </section>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" aria-label="Today's bins">
          <h2 className="font-semibold text-neutral-900">Today ({todayBins.length})</h2>
          {todayBins.length === 0 ? (
            <p className="text-sm text-neutral-500">No bins yet today.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {todayBins.map(b => (
                <li key={b.id} className="py-2 flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold text-neutral-900 w-16">{b.field_code ?? '—'}</span>
                  <span className="flex-1 min-w-0 truncate text-sm text-neutral-600">
                    {binPlace(b)}{b.fill_fraction < 1 ? ` · ${Math.round(b.fill_fraction * 100)}%` : ''}
                  </span>
                  {b.tag_code ? (
                    <span className="text-xs font-mono text-green-700">{b.tag_code}</span>
                  ) : b.status === 'started' ? (
                    <span className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => finish(b, 1)}>Full</Button>
                      <Button size="sm" variant="outline" onClick={() => finish(b, 0.5)}>Half</Button>
                    </span>
                  ) : (
                    <span className="text-xs text-neutral-500">{b.fill_fraction < 1 ? 'Part full' : 'Full'}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {scanning && <BarcodeScanner label="Scan picker card" onScan={onScan} onClose={() => setScanning(false)} />}
    </>
  );
}
